import type { Chord } from 'svguitar';
import { cn } from '@/lib/utils';

const STRING_X = (string: number) => 10 + (6 - string) * 8; // corda 6 à esquerda
const FRET_TOP = 18;
const FRET_H = 8;
const FRETS = 5;

/**
 * Diagrama de acorde em SVG próprio (60×70), no formato `Chord` do svguitar usado em
 * `src/lib/song/chords`: `fingers: [corda, casa | 'x' | 0, rótulo?]`, `barres`, `position`.
 * Casas são relativas a `position` (casa inicial exibida).
 */
export const ChordDiagram: React.FC<{
  shape: Chord;
  /** Para fundo branco (impressão): traços e dedos escuros. */
  onLight?: boolean;
  className?: string;
}> = ({ shape, onLight, className }) => {
  const ink = onLight
    ? {
        nut: '#111',
        string: 'rgba(0,0,0,.45)',
        fret: 'rgba(0,0,0,.3)',
        finger: '#111',
        fingerText: '#fff',
        barre: '#111',
        mute: '#444',
        open: '#111',
        label: '#444',
      }
    : {
        nut: '#D4D4D4',
        string: 'rgba(255,255,255,.35)',
        fret: 'rgba(255,255,255,.2)',
        finger: '#FFFFFF',
        fingerText: '#000',
        barre: '#5E98FF',
        mute: '#A8A8A8',
        open: '#D4D4D4',
        label: '#A8A8A8',
      };
  const position = shape.position && shape.position > 1 ? shape.position : 1;
  const bottom = FRET_TOP + FRETS * FRET_H;
  const fretY = (fret: number) => FRET_TOP + (fret - 0.5) * FRET_H;

  return (
    <svg
      viewBox="0 0 60 70"
      role="img"
      aria-label={`Diagrama de ${shape.title ?? 'acorde'}`}
      className={cn('h-[70px] w-[60px]', className)}
    >
      {/* trastes */}
      {Array.from({ length: FRETS + 1 }, (_, i) => (
        <line
          key={`fret-${i}`}
          x1={STRING_X(6)}
          x2={STRING_X(1)}
          y1={FRET_TOP + i * FRET_H}
          y2={FRET_TOP + i * FRET_H}
          stroke={i === 0 && position === 1 ? ink.nut : ink.fret}
          strokeWidth={i === 0 && position === 1 ? 2.4 : 1}
        />
      ))}

      {/* cordas */}
      {[6, 5, 4, 3, 2, 1].map((string) => (
        <line
          key={`string-${string}`}
          x1={STRING_X(string)}
          x2={STRING_X(string)}
          y1={FRET_TOP}
          y2={bottom}
          stroke={ink.string}
          strokeWidth={1}
        />
      ))}

      {position > 1 && (
        <text x={2} y={fretY(1) + 2} fontSize={6} fill={ink.label}>
          {position}
        </text>
      )}

      {/* pestanas */}
      {(shape.barres ?? []).map((barre, i) => {
        const x1 = Math.min(STRING_X(barre.fromString), STRING_X(barre.toString));
        const x2 = Math.max(STRING_X(barre.fromString), STRING_X(barre.toString));
        return (
          <rect
            key={`barre-${i}`}
            x={x1 - 3}
            y={fretY(barre.fret) - 3}
            width={x2 - x1 + 6}
            height={6}
            rx={3}
            fill={ink.barre}
          />
        );
      })}

      {/* dedos, cordas soltas e abafadas */}
      {shape.fingers.map(([string, fret, label], i) => {
        const x = STRING_X(string);

        if (fret === 'x') {
          return (
            <path
              key={`finger-${i}`}
              d={`M${x - 2.5} 7.5l5 5m0-5l-5 5`}
              stroke={ink.mute}
              strokeWidth={1.2}
              strokeLinecap="round"
            />
          );
        }

        if (fret === 0) {
          return (
            <circle
              key={`finger-${i}`}
              cx={x}
              cy={10}
              r={2.8}
              fill="none"
              stroke={ink.open}
              strokeWidth={1.2}
            />
          );
        }

        const text = typeof label === 'string' ? label : label?.text;

        return (
          <g key={`finger-${i}`}>
            <circle cx={x} cy={fretY(fret)} r={3.3} fill={ink.finger} />
            {text && (
              <text
                x={x}
                y={fretY(fret) + 2}
                fontSize={5}
                fontWeight={700}
                textAnchor="middle"
                fill={ink.fingerText}
              >
                {text}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};
